/**
 * RfqAiBuilderPanel — new "AI Builder" tab on RFQ Detail.
 *
 * Flow:
 *   1. Pick provider (Claude Sonnet 4.5 or GPT 5.2).
 *   2. Write a prompt describing the deliverable.
 *   3. Confirm which of the RFQ's attachments to include as context.
 *   4. Click Generate. Backend calls the LLM and returns a structured WBS.
 *   5. Edit rows inline (rename, tweak hours, add/remove effort rows).
 *   6. Push-to-Estimate: creates a new Estimate with per-designation
 *      sub-items and the rolled-up rates from designation master.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { fmtRelative } from "@/lib/format";
import { apiErrorMessage } from "@/lib/apiError";
import {
  Sparkles, Wand2, RefreshCcw, Trash2, Plus, ArrowRight, FileText,
  Bot, Cpu, Loader2, ChevronRight, ChevronDown, Info,
} from "lucide-react";

const PROVIDERS = [
  { v: "claude", label: "Claude Sonnet 4.5", hint: "Great at structured extraction, longer briefs" },
  { v: "gpt",    label: "GPT 5.2",           hint: "Fast, verbose, tight scoping" },
];

export default function RfqAiBuilderPanel({ rfq, onChanged }) {
  const nav = useNavigate();
  const { activeWorkspaceId: wsId } = useAuth();
  const [versions, setVersions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [active, setActive] = useState(null); // full version doc with tasks
  const [prompt, setPrompt] = useState("");
  const [provider, setProvider] = useState("claude");
  const [selectedFileIds, setSelectedFileIds] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [designations, setDesignations] = useState([]);
  const [expanded, setExpanded] = useState({}); // task id -> bool

  const attachments = rfq?.attachments || [];

  const loadVersions = useCallback(async () => {
    if (!rfq?.id) return;
    try {
      const { data } = await api.get(`/rfqs/${rfq.id}/ai-wbs`);
      const rows = data?.rows || [];
      setVersions(rows);
      // Auto-load the currently active one, else the newest
      const target = rows.find((r) => r.is_active) || rows[0];
      if (target && (!activeId || !rows.some((r) => r.id === activeId))) {
        setActiveId(target.id);
      }
    } catch {
      setVersions([]);
    }
  }, [rfq?.id, activeId]);

  useEffect(() => { loadVersions(); }, [loadVersions]);

  useEffect(() => {
    if (!activeId) { setActive(null); return; }
    api.get(`/rfq-wbs/${activeId}`)
      .then((r) => setActive(r.data))
      .catch(() => setActive(null));
  }, [activeId]);

  useEffect(() => {
    if (!wsId) return;
    api.get(`/workspaces/${wsId}/estimate-designations?active_only=true`)
      .then((r) => setDesignations(r.data || []))
      .catch(() => setDesignations([]));
  }, [wsId]);

  // Default select every attachment when RFQ changes
  useEffect(() => {
    setSelectedFileIds(attachments.map((a) => a.file_id));
  }, [rfq?.id, attachments.length]); // eslint-disable-line

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/rfqs/${rfq.id}/ai-wbs/generate`, {
        prompt: prompt.trim(),
        provider,
        file_ids: selectedFileIds,
      });
      toast.success(`WBS v${data.version_no} generated · ${data.tasks?.length || 0} tasks`);
      setActiveId(data.id);
      await loadVersions();
      setPrompt("");
    } catch (e) {
      toast.error(apiErrorMessage(e, "AI generation failed"));
    } finally { setGenerating(false); }
  };

  const setTaskField = (tid, path, val) => {
    if (!active) return;
    const next = { ...active, tasks: active.tasks.map((t) => {
      if (t.id !== tid) return t;
      if (path === "name") return { ...t, name: val };
      if (path === "description") return { ...t, description: val };
      if (path === "phase") return { ...t, phase: val };
      return t;
    })};
    setActive(next);
  };
  const setEffortField = (tid, idx, field, val) => {
    if (!active) return;
    const next = { ...active, tasks: active.tasks.map((t) => {
      if (t.id !== tid) return t;
      const rows = [...(t.effort_hours || [])];
      const row = { ...rows[idx], [field]: val };
      // If designation change, resolve id
      if (field === "designation") {
        const d = designations.find((x) => (x.name || "").toLowerCase() === (val || "").toLowerCase());
        row.designation_id = d?.id || null;
        row.matched = !!d;
      }
      if (field === "hours") row.hours = Number(val) || 0;
      rows[idx] = row;
      const total = rows.reduce((s, r) => s + (Number(r.hours) || 0), 0);
      return { ...t, effort_hours: rows, total_hours: Math.round(total * 100) / 100 };
    })};
    setActive(next);
  };
  const addEffortRow = (tid) => {
    if (!active) return;
    const next = { ...active, tasks: active.tasks.map((t) => t.id === tid ? {
      ...t,
      effort_hours: [...(t.effort_hours || []), { designation: designations[0]?.name || "", designation_id: designations[0]?.id || null, hours: 1, matched: !!designations[0] }],
    } : t)};
    setActive(next);
  };
  const removeEffortRow = (tid, idx) => {
    if (!active) return;
    const next = { ...active, tasks: active.tasks.map((t) => {
      if (t.id !== tid) return t;
      const rows = t.effort_hours.filter((_, i) => i !== idx);
      const total = rows.reduce((s, r) => s + (Number(r.hours) || 0), 0);
      return { ...t, effort_hours: rows, total_hours: Math.round(total * 100) / 100 };
    })};
    setActive(next);
  };
  const removeTask = (tid) => {
    if (!active) return;
    setActive({ ...active, tasks: active.tasks.filter((t) => t.id !== tid) });
  };
  const addTask = () => {
    if (!active) return;
    setActive({ ...active, tasks: [...(active.tasks || []), {
      id: `new-${Date.now()}`, name: "New task", description: "", phase: null,
      effort_hours: [], total_hours: 0,
    }]});
  };

  const saveWBS = async () => {
    if (!active) return;
    setSaving(true);
    try {
      await api.patch(`/rfq-wbs/${active.id}`, { tasks: active.tasks });
      toast.success("WBS saved");
      await loadVersions();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setSaving(false); }
  };

  const pushToEstimate = async () => {
    if (!active) return;
    if (!window.confirm(`Create a new Estimate with ${active.tasks?.length || 0} line item(s)?`)) return;
    setPushing(true);
    try {
      const { data } = await api.post(`/rfq-wbs/${active.id}/push-to-estimate`, {});
      toast.success(`Estimate ${data.estimate_number} created · ${data.line_count} lines · total ${Math.round((data.grand_total || 0)).toLocaleString()}`);
      onChanged?.();
      nav(`/app/estimates/${data.estimate_id}`);
    } catch (e) { toast.error(apiErrorMessage(e, "Push failed")); }
    finally { setPushing(false); }
  };

  const totalHours = useMemo(() => (active?.tasks || []).reduce((s, t) => s + (Number(t.total_hours) || 0), 0), [active?.tasks]);
  const totalCost = useMemo(() => {
    if (!active) return 0;
    let sum = 0;
    for (const t of active.tasks || []) {
      for (const e of t.effort_hours || []) {
        const d = designations.find((x) => x.id === e.designation_id
                                       || (x.name || "").toLowerCase() === (e.designation || "").toLowerCase());
        const rate = Number(d?.default_hourly_rate || 0);
        sum += (Number(e.hours) || 0) * rate;
      }
    }
    return Math.round(sum);
  }, [active, designations]);

  return (
    <div className="space-y-5" data-testid="rfq-ai-panel">
      {/* Prompt + generate */}
      <div className="border border-border p-4 space-y-3" data-testid="rfq-ai-composer">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide">
          <Sparkles className="w-3.5 h-3.5 text-primary" /> AI WBS builder
        </div>
        <div className="text-xs text-muted-foreground">
          Ask the AI to break down this RFQ into a work-breakdown-structure with
          effort per designation. It will use the attached brief(s) and the RFQ description
          for context. You can edit every row after generation, then push it to a new Estimate.
        </div>
        <div>
          <Label className="text-[10px] uppercase text-muted-foreground">Prompt</Label>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={4}
            placeholder="e.g. Break this brief into a full production plan. Include discovery, design, delivery, QA and launch."
            className="w-full text-sm px-3 py-2 mt-0.5 bg-background border border-border resize-y"
            data-testid="rfq-ai-prompt"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-[10px] uppercase text-muted-foreground">Model</Label>
            <div className="inline-flex border border-border rounded-none overflow-hidden w-full mt-0.5" data-testid="rfq-ai-provider">
              {PROVIDERS.map((p) => (
                <button
                  key={p.v}
                  type="button"
                  onClick={() => setProvider(p.v)}
                  title={p.hint}
                  className={`flex-1 px-2 py-1.5 text-xs ${provider === p.v ? "bg-foreground text-background" : "bg-background hover:bg-muted"}`}
                  data-testid={`rfq-ai-provider-${p.v}`}
                >
                  <span className="inline-flex items-center gap-1"><Cpu className="w-3 h-3" /> {p.label}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="md:col-span-2">
            <Label className="text-[10px] uppercase text-muted-foreground">
              Include attachments ({selectedFileIds.length}/{attachments.length})
            </Label>
            {attachments.length === 0 ? (
              <div className="text-[11px] text-muted-foreground italic mt-1 px-2 py-1 border border-dashed border-border">
                No attachments on this RFQ. Add briefs from the right rail to give the AI more context.
              </div>
            ) : (
              <div className="flex flex-wrap gap-1.5 mt-0.5" data-testid="rfq-ai-files">
                {attachments.map((a) => {
                  const on = selectedFileIds.includes(a.file_id);
                  return (
                    <button
                      key={a.file_id}
                      type="button"
                      onClick={() => setSelectedFileIds(on ? selectedFileIds.filter((x) => x !== a.file_id) : [...selectedFileIds, a.file_id])}
                      className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 border ${on ? "bg-foreground text-background border-foreground" : "bg-background border-border hover:bg-muted"}`}
                      data-testid={`rfq-ai-file-${a.file_id}`}
                    >
                      <FileText className="w-3 h-3" /> {a.filename}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={generate} disabled={generating}
                   className="bg-primary text-primary-foreground" data-testid="rfq-ai-generate">
            {generating ? (
              <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Generating…</>
            ) : (
              <><Wand2 className="w-3.5 h-3.5 mr-1.5" /> Generate WBS</>
            )}
          </Button>
          {designations.length === 0 && (
            <span className="text-[11px] text-amber-600 inline-flex items-center gap-1">
              <Info className="w-3 h-3" /> No designations configured — AI can still generate roles, but rates won't roll up until you add them.
            </span>
          )}
        </div>
      </div>

      {/* Version selector */}
      {versions.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap text-xs" data-testid="rfq-ai-versions">
          <span className="text-[10px] uppercase text-muted-foreground">Versions:</span>
          {versions.map((v) => (
            <button
              key={v.id}
              type="button"
              onClick={() => setActiveId(v.id)}
              className={`px-2 py-1 border ${activeId === v.id ? "bg-foreground text-background border-foreground" : "bg-background border-border hover:bg-muted"}`}
              data-testid={`rfq-ai-version-${v.id}`}
            >
              v{v.version_no} · {v.provider === "anthropic" ? "Claude" : v.provider === "openai" ? "GPT" : v.provider} · {fmtRelative(v.created_at)}
              {v.is_active && <span className="ml-1 text-[9px] text-emerald-500">●</span>}
              {v.pushed_to_estimate_id && <span className="ml-1 text-[9px] text-primary">→ EST</span>}
            </button>
          ))}
        </div>
      )}

      {/* WBS table */}
      {active && (
        <div className="border border-border" data-testid="rfq-ai-wbs">
          <div className="px-4 py-2 border-b border-border bg-muted/40 flex items-center justify-between flex-wrap gap-2">
            <div className="text-xs">
              <b>WBS v{active.version_no}</b> · {active.tasks?.length || 0} task(s) · <b>{totalHours}h</b> total
              {totalCost > 0 && <> · est. ₹{totalCost.toLocaleString()}</>}
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={addTask} data-testid="rfq-ai-add-task">
                <Plus className="w-3.5 h-3.5 mr-1.5" /> Add task
              </Button>
              <Button size="sm" variant="outline" onClick={saveWBS} disabled={saving}
                       data-testid="rfq-ai-save">
                {saving ? "Saving…" : "Save changes"}
              </Button>
              <Button size="sm" onClick={pushToEstimate} disabled={pushing || !active.tasks?.length}
                       className="bg-emerald-600 text-white hover:bg-emerald-500"
                       data-testid="rfq-ai-push">
                {pushing ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Pushing…</> :
                  <><ArrowRight className="w-3.5 h-3.5 mr-1.5" /> Push to Estimate</>}
              </Button>
            </div>
          </div>

          {(active.summary || (active.assumptions || []).length > 0 || (active.risks || []).length > 0) && (
            <div className="px-4 py-3 border-b border-border bg-muted/10 text-xs space-y-2" data-testid="rfq-ai-context">
              {active.summary && <div><b>Summary:</b> {active.summary}</div>}
              {(active.assumptions || []).length > 0 && (
                <div><b>Assumptions:</b> {(active.assumptions || []).join(" · ")}</div>
              )}
              {(active.risks || []).length > 0 && (
                <div><b>Risks:</b> {(active.risks || []).join(" · ")}</div>
              )}
              {active.recommended_timeline_weeks && (
                <div><b>Recommended timeline:</b> {active.recommended_timeline_weeks} week(s)</div>
              )}
            </div>
          )}

          <div className="divide-y divide-border">
            {(active.tasks || []).map((t, idx) => {
              const open = expanded[t.id] !== false; // default open
              return (
                <div key={t.id} className="p-3 hover:bg-muted/10" data-testid={`rfq-ai-task-${t.id}`}>
                  <div className="flex items-start gap-2">
                    <button type="button" onClick={() => setExpanded({ ...expanded, [t.id]: !open })}
                             className="mt-1 text-muted-foreground hover:text-foreground"
                             aria-label="Toggle">
                      {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] text-muted-foreground w-6 tabular-nums">#{idx + 1}</span>
                        <input
                          value={t.name}
                          onChange={(e) => setTaskField(t.id, "name", e.target.value)}
                          className="flex-1 min-w-0 text-sm font-medium px-2 py-1 bg-background border border-transparent hover:border-border focus:border-primary focus:outline-none"
                          data-testid={`rfq-ai-task-name-${t.id}`}
                        />
                        {t.phase && (
                          <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 border border-border">
                            {t.phase}
                          </span>
                        )}
                        <span className="text-[11px] text-muted-foreground tabular-nums font-mono">
                          {t.total_hours || 0}h
                        </span>
                        <button type="button" onClick={() => removeTask(t.id)}
                                 className="text-muted-foreground hover:text-rose-500 p-1"
                                 aria-label="Remove"
                                 data-testid={`rfq-ai-task-remove-${t.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      {open && (
                        <div className="mt-2 ml-8 space-y-2">
                          <textarea
                            value={t.description || ""}
                            onChange={(e) => setTaskField(t.id, "description", e.target.value)}
                            placeholder="Task description (optional)"
                            rows={2}
                            className="w-full text-xs px-2 py-1 bg-background border border-border resize-y"
                            data-testid={`rfq-ai-task-desc-${t.id}`}
                          />
                          <div className="border border-border">
                            <div className="px-2 py-1 bg-muted/20 border-b border-border text-[10px] uppercase text-muted-foreground grid grid-cols-[1fr_100px_90px_28px] gap-2">
                              <div>Designation</div>
                              <div className="text-right">Hours</div>
                              <div className="text-right">Rate</div>
                              <div></div>
                            </div>
                            {(t.effort_hours || []).length === 0 && (
                              <div className="p-2 text-[11px] text-muted-foreground italic">
                                No effort assigned. Click Add effort to add a designation.
                              </div>
                            )}
                            {(t.effort_hours || []).map((e, i) => {
                              const d = designations.find((x) => x.id === e.designation_id
                                                             || (x.name || "").toLowerCase() === (e.designation || "").toLowerCase());
                              const rate = Number(d?.default_hourly_rate || 0);
                              return (
                                <div key={i} className="grid grid-cols-[1fr_100px_90px_28px] gap-2 items-center px-2 py-1 border-b border-border/50 last:border-b-0"
                                     data-testid={`rfq-ai-effort-${t.id}-${i}`}>
                                  <div>
                                    <select
                                      value={e.designation || ""}
                                      onChange={(ev) => setEffortField(t.id, i, "designation", ev.target.value)}
                                      className={`w-full text-xs px-1 py-1 bg-background border ${d ? "border-border" : "border-amber-400"}`}
                                      data-testid={`rfq-ai-effort-${t.id}-${i}-desig`}
                                    >
                                      {!d && <option value={e.designation}>{e.designation} (not in master)</option>}
                                      {designations.map((x) => (
                                        <option key={x.id} value={x.name}>{x.name}</option>
                                      ))}
                                    </select>
                                  </div>
                                  <input
                                    type="number" step="0.5" min="0" value={e.hours || 0}
                                    onChange={(ev) => setEffortField(t.id, i, "hours", ev.target.value)}
                                    className="w-full text-xs px-1 py-1 bg-background border border-border text-right tabular-nums"
                                    data-testid={`rfq-ai-effort-${t.id}-${i}-hours`}
                                  />
                                  <div className="text-[11px] text-right tabular-nums text-muted-foreground">
                                    {rate ? `₹${rate}/h` : "—"}
                                  </div>
                                  <button type="button" onClick={() => removeEffortRow(t.id, i)}
                                           className="text-muted-foreground hover:text-rose-500"
                                           aria-label="Remove"
                                           data-testid={`rfq-ai-effort-${t.id}-${i}-remove`}>
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </div>
                              );
                            })}
                            <div className="p-1 border-t border-border">
                              <button
                                type="button"
                                onClick={() => addEffortRow(t.id)}
                                className="text-[11px] px-2 py-1 border border-border hover:bg-muted"
                                data-testid={`rfq-ai-effort-add-${t.id}`}
                              >
                                + Add effort
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            {(active.tasks || []).length === 0 && (
              <div className="p-8 text-center text-xs text-muted-foreground">
                Empty WBS. Click <b>Add task</b> above or generate a new one.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
